import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Tab, Tabs, Button, Badge, Modal } from "react-bootstrap";
import {
  FaMoneyBillWave, FaEye, FaCheck, FaTrashAlt, FaPrint, FaEdit,
  FaMoneyCheckAlt, FaExchangeAlt, FaPlus, FaMinus, FaCalendarAlt, FaInfoCircle, FaClock,
  FaArrowRight, FaCreditCard, FaUniversity, FaWallet, FaUserTie, FaUserAstronaut, FaHistory
} from "react-icons/fa";

import { useNavigate, useSearchParams } from "react-router-dom";
import BrandHeader from "../core/components/Brand/BrandHeader";
import BrandTable from "../core/components/BrandTable/BrandTable";
import SharedModal from "../core/shared/components/Modal";
import TransactionForm from "../transections/TransactionForm";
import PaymentDetailsCard from "./PaymentDetailsCard";
import { useDispatch, useSelector } from "react-redux";
import { fetchTransactionsPaginated, deleteTransaction } from "../../redux/features/transactions.features";
import { fetchPaymentsPaginated, deletePayment } from "../../redux/features/payments.features";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { formatCurrency } from "../../utility";

const PaymentTransactionList = ({ propSearch, hideHeader, hideTransactionsTab }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const searchQuery = propSearch || searchParams.get("search");

  // Enhanced Summary Icon
  const SummaryIcon = ({ icon: Icon, color, bgGradient }) => (
    <div
      className={`avatar avatar-lg rounded-3 d-flex align-items-center justify-content-center text-white shadow-sm animate__animated animate__zoomIn`}
      style={{
        background: bgGradient || `var(--bs-${color})`,
        width: '56px',
        height: '56px'
      }}
    >
      <Icon size={28} />
    </div>
  );


  // Data Selectors
  const { transactions, loading: txLoading, pagination: txPagination } = useSelector((state) => state.transactionsReducer);
  const { payments: allPayments, loading: payLoading, pagination: payPagination } = useSelector((state) => state.paymentsReducer);

  // Pagination & Filter State
  const [payPage, setPayPage] = useState(1);
  const [txPage, setTxPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState(searchQuery || "");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });

  // State
  const [activeTab, setActiveTab] = useState("payments");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false); // NEW STATE
  const [transactionModalMode, setTransactionModalMode] = useState("create"); // create | edit

  // Refs for Modals
  // const viewPaymentRef = useRef(); // No longer needed
  const viewTxRef = useRef();
  const transactionFormRef = useRef(); // If needed to trigger form submit externally or just control visibility via state



  useEffect(() => {
    dispatch(fetchPaymentsPaginated({
      page: payPage,
      limit,
      search: searchTerm,
      start_date: dateRange.start,
      end_date: dateRange.end
    }));
  }, [dispatch, payPage, limit, searchTerm, dateRange]);

  useEffect(() => {
    dispatch(fetchTransactionsPaginated({
      page: txPage,
      limit,
      search: searchTerm,
      start_date: dateRange.start,
      end_date: dateRange.end
    }));
  }, [dispatch, txPage, limit, searchTerm, dateRange]);



  // --- Actions Handlers ---

  const handlePaymentAction = (action, row) => {
    switch (action) {
      case "View":
        setSelectedItem(row);
        setShowPaymentModal(true);
        break;
      case "Edit":
        // For now, there is no edit route configured in routes/index.js for payments with ID
        // and PaymentForm expects a prop. We could navigate to a new route if created, 
        // or show a warning that edit is not fully implemented via route yet.
        // Let's assume we can navigate to AddPayments (which is PaymentForm) but populating it requires state/prop.
        // Simpler for now: Show basic edit or just delete. 
        // Or if the user wants full edit, we might need a modal or a route fix.
        // Leaving as log for safety unless routes are fixed.
        toast.info("Edit Payment via Modal/Route to be implemented. Using View for now.");
        break;
      case "Delete":
        confirmDelete("payment", row._id);
        break;
      default:
        break;
    }
  };

  const handleTransactionAction = (action, row) => {
    switch (action) {
      case "View":
        setSelectedItem(row);
        if (viewTxRef.current) viewTxRef.current.showModal();
        break;
      case "Edit":
        setSelectedItem(row);
        setTransactionModalMode("edit");
        setShowTransactionModal(true);
        break;
      case "Delete":
        confirmDelete("transaction", row._id);
        break;
      default:
        break;
    }
  };

  const confirmDelete = (type, id) => {
    confirmAlert({
      title: `Delete ${type === 'payment' ? 'Payment' : 'Transaction'}?`,
      message: "Are you sure you want to delete this record? This cannot be undone.",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            if (type === 'payment') {
              dispatch(deletePayment(id)).then(() => toast.success("Payment deleted"));
            } else {
              dispatch(deleteTransaction(id)).then(() => toast.success("Transaction deleted"));
            }
          },
        },
        { label: "No" },
      ],
    });
  };

  // --- Columns Definitions ---

  const paymentColumns = [
    {
      key: "icon",
      label: "",
      width: "50px",
      render: () => (
        <div className="avatar avatar-sm border rounded-circle bg-light overflow-hidden">
          <div className="d-flex align-items-center justify-content-center h-100 text-success">
            <FaMoneyCheckAlt size={18} />
          </div>
        </div>
      )
    },
    {
      key: "transaction_id",
      label: "#ID",
      render: (val, row) => <span className="fw-bold text-primary font-monospace small">{row.payment_id || val}</span>
    },
    {
      key: "transaction_date",
      label: "Date",
      sortable: true,
      render: (val) => (
        <div className="d-flex flex-column">
          <span className="fw-semibold text-dark">{new Date(val).toLocaleDateString()}</span>
          <span className="x-small text-muted">{new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      )
    },
    {
      key: "accounts",
      label: "Flow (From → To)",
      render: (_, row) => (
        <div className="d-flex align-items-center gap-2">
          <Badge bg="light" className="text-dark border font-monospace py-1 px-2" style={{ fontSize: '0.7rem' }}>
            {row.from_account_id?.account_name?.slice(0, 15) || "N/A"}
          </Badge>
          <FaArrowRight size={10} className="text-muted" />
          <Badge bg="primary" className="bg-opacity-10 text-white border border-primary border-opacity-25 font-monospace py-1 px-2" style={{ fontSize: '0.7rem' }}>
            {row.to_account_id?.account_name?.slice(0, 15) || "N/A"}
          </Badge>
        </div>
      )
    },
    {
      key: "methods",
      label: "Methods",
      render: (_, row) => {
        const methods = row.payment_methods || [];
        return (
          <div className="d-flex gap-1">
            {methods.map((m, idx) => {
              const getMethodIcon = (type) => {
                switch (type) {
                  case 'Cash': return <FaMoneyBillWave className="text-success" title="Cash" />;
                  case 'Card': return <FaCreditCard className="text-primary" title="Card" />;
                  case 'Bank Transfer': return <FaUniversity className="text-info" title="Bank Transfer" />;
                  case 'Cheque': return <FaMoneyCheckAlt className="text-warning" title="Cheque" />;
                  default: return <FaWallet className="text-muted" />;
                }
              };
              return (
                <div key={idx} className="p-1 px-2 bg-light rounded border d-flex align-items-center" style={{ scale: '0.9' }}>
                  {getMethodIcon(m.method)}
                </div>
              );
            })}
          </div>
        );
      }
    },
    {
      key: "transaction_type",
      label: "Category",
      sortable: true,
      render: (val) => {
        const getBrandColor = (type) => {
          if (type === 'Sale' || type === 'Deposit') return 'success';
          if (type === 'Supplier Payment' || type === 'Withdrawal') return 'danger';
          return 'info';
        };
        return (
          <Badge
            bg={getBrandColor(val)}
            className="rounded-pill px-3 py-1 fw-bold text-uppercase"
            style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}
          >
            {val}
          </Badge>
        );
      }
    },

    {
      key: "amount",
      label: "Amount",
      sortable: true,
      render: (val) => formatCurrency(val)
    },
    {
      key: "references",
      label: "Reference",
      render: (_, row) => {
        if (row.references?.supplier?.supplier_name) return row.references.supplier.supplier_name;
        if (row.references?.customer?.customer_name) return row.references.customer.customer_name;
        if (row.references?.employee?.first_name) return `${row.references.employee.first_name} ${row.references.employee.last_name}`;
        return "N/A";
      }
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      render: (_, row) => (
        <div className="d-flex gap-2 justify-content-end">
          <Button variant="outline-primary" size="sm" onClick={() => handlePaymentAction("View", row)} title="View Detail">
            <FaEye />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => handlePaymentAction("Delete", row)} title="Delete">
            <FaTrashAlt />
          </Button>
        </div>
      )
    }
  ];

  const transactionColumns = [
    {
      key: "icon",
      label: "",
      width: "50px",
      render: (val, row) => (
        <div className="avatar avatar-sm border rounded-circle bg-light overflow-hidden">
          <div className={`d-flex align-items-center justify-content-center h-100 ${row.transaction_type === 'Deposit' ? 'text-success' : 'text-danger'}`}>
            {row.transaction_type === 'Deposit' ? <FaPlus /> : <FaExchangeAlt />}
          </div>
        </div>
      )
    },
    {
      key: "date",
      label: "Date",
      sortable: true,
      render: (val) => (
        <div className="d-flex flex-column">
          <span className="fw-semibold text-dark">{val ? new Date(val).toLocaleDateString() : "N/A"}</span>
          <span className="x-small text-muted">{val ? new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}</span>
        </div>
      )
    },
    {
      key: "transaction_type",
      label: "Type",
      sortable: true,
      render: (val) => (
        <Badge bg={val === "Deposit" ? "success" : "danger"} className="rounded-pill px-2">
          {val === 'Deposit' ? <FaPlus size={8} className="me-1" /> : <FaMinus size={8} className="me-1" />}
          {val}
        </Badge>
      )
    },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      render: (val, row) => (
        <span className={`fw-bold ${row.transaction_type === 'Withdrawal' ? "text-danger" : "text-success"}`}>
          {row.transaction_type === 'Withdrawal' ? '-' : '+'} {formatCurrency(Math.abs(val))}
        </span>
      )
    },
    {
      key: "account_id",
      label: "Ledger",
      render: (val) => (
        <Badge bg="light" className="text-dark border font-monospace py-1 px-2" style={{ fontSize: '0.75rem' }}>
          <FaUniversity size={10} className="me-1 text-muted" />
          {val?.account_name || "Unknown"}
        </Badge>
      )
    },
    {
      key: "reason",
      label: "Description",
      render: (val) => (
        <div className="text-muted small text-truncate d-flex align-items-center" style={{ maxWidth: '200px' }}>
          <FaInfoCircle className="me-1 text-opacity-50" size={10} />
          {val || "N/A"}
        </div>
      )
    },

    {
      key: "status",
      label: "Status",
      render: (val) => <Badge bg="secondary text-uppercase" style={{ fontSize: '0.65rem' }}>{val || "Completed"}</Badge>
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      render: (_, row) => (
        <div className="d-flex gap-2 justify-content-end">
          <Button variant="outline-info" size="sm" onClick={() => handleTransactionAction("View", row)}>
            <FaEye />
          </Button>
          <Button variant="outline-primary" size="sm" onClick={() => handleTransactionAction("Edit", row)}>
            <FaEdit />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => handleTransactionAction("Delete", row)}>
            <FaTrashAlt />
          </Button>
        </div>
      )
    }
  ];

  // --- Header Actions ---

  const headerActions = [
    {
      label: "New Payment",
      icon: <FaMoneyCheckAlt />,
      variant: "primary",
      onClick: () => navigate("/AddPayments"), // Navigate to the full form page
      show: activeTab === "payments"
    },
    {
      label: "New Transaction",
      icon: <FaExchangeAlt />,
      variant: "success",
      onClick: () => {
        setTransactionModalMode("create");
        setSelectedItem(null);
        setShowTransactionModal(true);
      },
      show: true // Always show, or maybe just when transactions tab is active? Let's show always or per tab.
      // Better to check active tab
    }
  ].filter(action => action.show && (activeTab === "payments" ? action.label === "New Payment" : action.label === "New Transaction"));


  return (
    <div className={hideHeader ? "container-fluid p-0" : "container-xxl flex-grow-1 container-p-y"}>
      {!hideHeader && (
        <BrandHeader
          title="Finance & Payments"
          subtitle="Manage payments, deposits, and withdrawals."
          icon={<FaMoneyBillWave />}
          breadcrumbs={[
            { label: "Home", to: "/" },
            { label: "Finance", active: true }
          ]}
          actions={headerActions}
        />
      )}

      {/* Financial Overview Summary Cards */}
      {!hideHeader && (
        <Row className="mb-4 g-4 animate__animated animate__fadeIn">
          <Col md={4}>
            <Card className="border-0 shadow-lg h-100 overflow-hidden" style={{ borderRadius: '16px' }}>
              <div className="position-absolute top-0 end-0 p-3 opacity-10">
                <FaPlus size={80} />
              </div>
              <Card.Body className="d-flex align-items-center p-4">
                <SummaryIcon
                  icon={FaHistory}
                  bgGradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                />
                <div className="ms-4">
                  <p className="text-muted mb-0 x-small fw-bold text-uppercase" style={{ letterSpacing: '1px' }}>Total Inflow</p>
                  <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: '-1px' }}>
                    {formatCurrency(allPayments.filter(p => ['Deposit', 'Sale'].includes(p.transaction_type)).reduce((sum, p) => sum + p.amount, 0))}
                  </h3>
                  <div className="text-success small fw-bold">
                    <FaPlus size={10} className="me-1" /> Revenue & Deposits
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-lg h-100 overflow-hidden" style={{ borderRadius: '16px' }}>
              <div className="position-absolute top-0 end-0 p-3 opacity-10">
                <FaMinus size={80} />
              </div>
              <Card.Body className="d-flex align-items-center p-4">
                <SummaryIcon
                  icon={FaExchangeAlt}
                  bgGradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                />
                <div className="ms-4">
                  <p className="text-muted mb-0 x-small fw-bold text-uppercase" style={{ letterSpacing: '1px' }}>Total Outflow</p>
                  <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: '-1px' }}>
                    {formatCurrency(allPayments.filter(p => ['Withdrawal', 'Supplier Payment', 'Employees Payment'].includes(p.transaction_type)).reduce((sum, p) => sum + p.amount, 0))}
                  </h3>
                  <div className="text-danger small fw-bold">
                    <FaMinus size={10} className="me-1" /> Expenses & Payouts
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-lg h-100 overflow-hidden" style={{ borderRadius: '16px', borderLeft: '5px solid #6366f1!important' }}>
              <div className="position-absolute top-0 end-0 p-3 opacity-10">
                <FaMoneyBillWave size={80} />
              </div>
              <Card.Body className="d-flex align-items-center p-4">
                <SummaryIcon
                  icon={FaUniversity}
                  bgGradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
                />
                <div className="ms-4">
                  <p className="text-muted mb-0 x-small fw-bold text-uppercase" style={{ letterSpacing: '1px' }}>Net Liquidity</p>
                  <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: '-1px' }}>
                    {formatCurrency(allPayments.reduce((sum, p) => sum + (['Withdrawal', 'Supplier Payment', 'Employees Payment'].includes(p.transaction_type) ? -p.amount : p.amount), 0))}
                  </h3>
                  <div className="text-primary small fw-bold">
                    <FaCheck size={10} className="me-1" /> Book Balance
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}


      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4 intel-tabs" // Use custom class for better styling
      >
        <Tab
          eventKey="payments"
          title={
            <div className="d-flex align-items-center gap-2">
              Payments
              <Badge bg="primary" className="rounded-pill px-2 py-1" style={{ fontSize: '0.6rem' }}>
                {payPagination.totalRecords || 0}
              </Badge>
            </div>
          }
        >

          <BrandTable
            title="Payments List"
            data={allPayments}
            columns={paymentColumns}
            loading={payLoading}
            pagination={true}
            serverSide={true}
            currentPage={payPagination.currentPage}
            totalRecords={payPagination.totalRecords}
            rowsPerPage={limit}
            onPageChange={(p) => setPayPage(p)}
            onRowsPerPageChange={(l) => { setLimit(l); setPayPage(1); }}
            searchable={true}
            onSearch={(s) => { setSearchTerm(s); setPayPage(1); setTxPage(1); }}
            exportable={true}
            searchPlaceholder="Search payments (ID, Description, Agent)..."
            emptyMessage="No payments found."
          />
        </Tab>
        {!hideTransactionsTab && (
          <Tab
            eventKey="transactions"
            title={
              <div className="d-flex align-items-center gap-2">
                External Transactions
                <Badge bg="secondary" className="rounded-pill px-2 py-1" style={{ fontSize: '0.6rem' }}>
                  {txPagination.totalRecords || 0}
                </Badge>
              </div>
            }
          >

            <BrandTable
              title="Transactions List"
              data={transactions}
              columns={transactionColumns}
              loading={txLoading}
              pagination={true}
              serverSide={true}
              currentPage={txPagination.currentPage}
              totalRecords={txPagination.totalRecords}
              rowsPerPage={limit}
              onPageChange={(p) => setTxPage(p)}
              onRowsPerPageChange={(l) => { setLimit(l); setTxPage(1); }}
              searchable={true}
              onSearch={(s) => { setSearchTerm(s); setPayPage(1); setTxPage(1); }}
              exportable={true}
              searchPlaceholder="Search transactions..."
              emptyMessage="No transactions found."
            />
          </Tab>
        )}
      </Tabs>

      {/* --- Modals --- */}



      {/* View Payment Modal - Fullscreen for polished view */}
      {showPaymentModal && selectedItem && (
        <PaymentDetailsCard data={selectedItem} onClose={() => setShowPaymentModal(false)} />
      )}

      {/* View Transaction Modal */}
      <SharedModal ref={viewTxRef} title="Transaction Details" size="md">
        {selectedItem && (
          <div className="p-3 animate__animated animate__fadeIn">
            <style>
              {`
                .tx-detail-card {
                  background: #f8f9fa;
                  border-radius: 12px;
                  border: 1px solid #e9ecef;
                  padding: 20px;
                }
                .tx-label {
                  font-size: 0.75rem;
                  color: #8592a3;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  font-weight: 700;
                  margin-bottom: 2px;
                }
                .tx-value {
                  font-size: 1rem;
                  color: #566a7f;
                  font-weight: 600;
                }
                .tx-amount-hero {
                  font-size: 2rem;
                  font-weight: 800;
                  margin-bottom: 20px;
                  padding: 20px;
                  background: white;
                  border-radius: 12px;
                  text-align: center;
                  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
                }
              `}
            </style>

            <div className={`tx-amount-hero ${selectedItem.transaction_type === 'Deposit' ? 'text-success' : 'text-danger'}`}>
              <div className="tx-label text-center">Amount</div>
              {selectedItem.transaction_type === 'Deposit' ? '+' : '-'} {formatCurrency(Math.abs(selectedItem.amount))}
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div className="tx-detail-card h-100">
                  <div className="tx-label">
                    <FaInfoCircle className="me-1" /> Transaction ID
                  </div>
                  <div className="tx-value font-monospace small">
                    {selectedItem.transaction_id || selectedItem._id}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="tx-detail-card h-100">
                  <div className="tx-label">
                    <FaExchangeAlt className="me-1" /> Type
                  </div>
                  <div className="tx-value text-capitalize">
                    <Badge bg={selectedItem.transaction_type === 'Deposit' ? 'success' : 'warning'} className="shadow-none">
                      {selectedItem.transaction_type}
                    </Badge>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="tx-detail-card h-100">
                  <div className="tx-label">
                    <FaCalendarAlt className="me-1" /> Date
                  </div>
                  <div className="tx-value">
                    {selectedItem.transaction_date ? new Date(selectedItem.transaction_date).toLocaleDateString() : (selectedItem.createdAt ? new Date(selectedItem.createdAt).toLocaleDateString() : "N/A")}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="tx-detail-card h-100">
                  <div className="tx-label">
                    <FaClock className="me-1" /> Time
                  </div>
                  <div className="tx-value">
                    {selectedItem.transaction_date ? new Date(selectedItem.transaction_date).toLocaleTimeString() : (selectedItem.createdAt ? new Date(selectedItem.createdAt).toLocaleTimeString() : "N/A")}
                  </div>
                </div>
              </Col>
              <Col md={12}>
                <div className="tx-detail-card">
                  <div className="tx-label">
                    <FaMoneyBillWave className="me-1" /> Reason / Description
                  </div>
                  <div className="tx-value" style={{ fontWeight: 400, color: '#697a8d' }}>
                    {selectedItem.reason || "No description provided."}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="tx-detail-card h-100">
                  <div className="tx-label">
                    Balance After
                  </div>
                  <div className="tx-value fw-bold text-primary">
                    {formatCurrency(selectedItem.balance_after_transaction || 0)}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="tx-detail-card h-100">
                  <div className="tx-label">
                    Account
                  </div>
                  <div className="tx-value">
                    {selectedItem.account_id?.account_name || "N/A"}
                  </div>
                </div>
              </Col>
              {selectedItem.segment_id && (
                <Col md={6}>
                  <div className="tx-detail-card h-100">
                    <div className="tx-label">Segment</div>
                    <div className="tx-value">{selectedItem.segment_id}</div>
                  </div>
                </Col>
              )}
              {selectedItem.agent_id && (
                <Col md={6}>
                  <div className="tx-detail-card h-100">
                    <div className="tx-label">Agent ID</div>
                    <div className="tx-value small font-monospace">{selectedItem.agent_id}</div>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}
      </SharedModal>

      {/* Create/Edit Transaction Modal (Using Bootstrap Modal wrapper since TransactionForm doesn't seem to be a modal itself, or we wrap it) */}
      <div className={`modal fade ${showTransactionModal ? 'show d-block' : ''}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{transactionModalMode === 'create' ? 'New Transaction' : 'Edit Transaction'}</h5>
              <button type="button" className="btn-close" onClick={() => setShowTransactionModal(false)}></button>
            </div>
            <div className="modal-body">
              {/* 
                  TransactionForm might assume it's standalone. 
                  We pass props if it supports them. 
                  Checking previous code, it receives `initialValues` and `isEdit`.
               */}
              {showTransactionModal && (
                <TransactionForm
                  isEdit={transactionModalMode === 'edit'}
                  initialValues={transactionModalMode === 'edit' ? selectedItem : null}
                  onSubmitSuccess={() => {
                    setShowTransactionModal(false);
                    dispatch(fetchTransactionsPaginated());
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PaymentTransactionList;
